import React, { useEffect, useMemo, useRef, useState } from "react";
import { useEmailBuilder } from "./useEmailBuilder";
import { mergeCatalog, KeywordLike } from "./emailVars";
import { useCaretInsert } from "./useCaretInsert";
import { Canvas } from "./Canvas";
import { ServerPreview } from "./ServerPreview";
import { Palette } from "./Palette";
import { Inspector } from "./Inspector";
import { getRequiredVars } from "./templateGroup";
import { isBuilderCompatible } from "../blockModel/parse";
import {
	Topbar,
	TopbarTitle,
	TopbarSpacer,
	SegToggle,
	SegBtn,
	IconBtn,
	PresetBtn,
	SaveBtn,
	ShellWrap,
	PaneRow,
	SidePanel,
	CanvasArea,
	CanvasAreaDark,
} from "./builderShellStyled";
import { EmailVersion } from "../../../../services/EmailSerivce";
import { VersionHistoryModal } from "./VersionHistoryModal";
import { EMAIL_PRESETS } from "./emailPresets";
import { EmailBuilderActions } from "./useEmailBuilder";

// ---------------------------------------------------------------------------
// PresetsMenu — small dropdown that lists the three layout presets.
// ---------------------------------------------------------------------------

interface PresetsMenuProps {
	actions: EmailBuilderActions;
}

function PresetsMenu({ actions }: PresetsMenuProps): JSX.Element {
	const [open, setOpen] = useState(false);
	const ref = useRef<HTMLDivElement>(null);

	// Close when clicking outside.
	useEffect(() => {
		if (!open) return;
		const handler = (e: MouseEvent) => {
			if (ref.current && !ref.current.contains(e.target as Node)) {
				setOpen(false);
			}
		};
		document.addEventListener("mousedown", handler);
		return () => document.removeEventListener("mousedown", handler);
	}, [open]);

	const handleSelect = (presetId: string) => {
		const preset = EMAIL_PRESETS.find((p) => p.id === presetId);
		if (!preset) return;
		setOpen(false);
		if (!window.confirm("Apply this layout? Your text stays — only the look changes. Undo with Ctrl+Z.")) {
			return;
		}
		actions.applyPreset(presetId);
	};

	return (
		<div
			ref={ref}
			style={{ position: "relative" }}>
			<PresetBtn
				title="Choose a ready-made email template"
				active={open}
				onClick={() => setOpen((p) => !p)}
				aria-label="Templates"
				data-testid="btn-presets">
				⊞ Templates ▾
			</PresetBtn>
			{open && (
				<div
					style={{
						position: "absolute",
						top: "calc(100% + 6px)",
						right: 0,
						background: "#fff",
						border: "1px solid #e3e8ef",
						borderRadius: 10,
						boxShadow: "0 6px 24px rgba(15,23,42,0.13)",
						zIndex: 200,
						minWidth: 240,
						padding: "6px 0",
					}}
					data-testid="presets-menu">
					<div
						style={{
							fontSize: 10,
							fontWeight: 700,
							letterSpacing: "0.06em",
							color: "#94a3b8",
							padding: "6px 14px 4px",
						}}>
						TEMPLATES
					</div>
					{EMAIL_PRESETS.map((preset) => (
						<button
							key={preset.id}
							type="button"
							data-testid={"preset-" + preset.id}
							style={{
								display: "block",
								width: "100%",
								textAlign: "left",
								padding: "8px 14px",
								background: "none",
								border: "none",
								cursor: "pointer",
								fontSize: 13,
							}}
							onMouseEnter={(e) => {
								(e.currentTarget as HTMLButtonElement).style.background = "#f1f5f9";
							}}
							onMouseLeave={(e) => {
								(e.currentTarget as HTMLButtonElement).style.background = "none";
							}}
							onClick={() => handleSelect(preset.id)}>
							<div style={{ fontWeight: 700, color: "#1e293b" }}>{preset.label}</div>
							<div style={{ fontSize: 11.5, color: "#64748b", marginTop: 2 }}>{preset.description}</div>
						</button>
					))}
				</div>
			)}
		</div>
	);
}

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface EmailBuilderProps {
	content: string;
	group: string;
	onSave: (content: string) => void;
	/** Called (debounced ~300 ms) whenever the builder content changes. */
	onChange?: (content: string, missing: string[]) => void;
	/**
	 * Imperative ref set by EmailBuilder to a live getter that returns the
	 * current serialized content without waiting for the debounce.
	 * Used by DialogUpdateContentEmail.getCurrentContent() to avoid the
	 * 300 ms debounce race when the user clicks Save quickly after typing.
	 */
	getContentRef?: React.MutableRefObject<(() => string) | null>;
	/** If provided, shows "Lưu nháp" button; called with live content. */
	onSaveDraft?: (content: string) => void;
	/** If provided, shows "Xuất bản" button; called with live content after gate. */
	onPublish?: (content: string) => void;
	/** If provided, shows "Lịch sử" button; must return the version list. */
	onListVersions?: () => Promise<EmailVersion[]>;
	/** Called when user clicks Restore inside VersionHistoryModal. */
	onRestore?: (versionId: number) => void;
	/**
	 * Called when the user clicks "Tạo lại bằng Builder" on the incompatible-content
	 * guard (raw-HTML legacy templates). Should replace content with a builder-editable
	 * starter so the builder can open it normally.
	 */
	onRegenerate?: () => void;
	/** Full backend keyword list — merged into the variable palette so every ${...} is available. */
	keywords?: KeywordLike[];
	/**
	 * Template id. When present, the "Xem" tab renders through the BACKEND preview
	 * endpoint instead of the React canvas, so what the user sees is what the send
	 * pipeline produces — including markup blocks the canvas cannot draw.
	 */
	templateId?: number;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function EmailBuilder({
	content,
	group,
	onSave,
	onChange,
	getContentRef,
	onSaveDraft,
	onPublish,
	onListVersions,
	onRestore,
	onRegenerate,
	keywords,
	templateId,
}: EmailBuilderProps): JSX.Element {
	const compatible = isBuilderCompatible(content);
	const { state, mode, darkMode, sampleMode, canUndo, canRedo, actions } = useEmailBuilder(content, group);
	// Full catalog = curated group vars + every backend keyword (so nothing is "missing").
	const catalog = useMemo(() => mergeCatalog(group, keywords), [group, keywords]);
	const { rememberFocus, insertChip, insertText } = useCaretInsert(actions);

	const [historyVersions, setHistoryVersions] = useState<EmailVersion[] | null>(null);
	const [historyLoading, setHistoryLoading] = useState(false);
	const [publishAlert, setPublishAlert] = useState("");
	const [draftSaving, setDraftSaving] = useState(false);
	const [publishing, setPublishing] = useState(false);

	// Keyboard shortcuts: Ctrl/Cmd+Z = undo, Ctrl/Cmd+Shift+Z or Ctrl+Y = redo
	const containerRef = useRef<HTMLDivElement>(null);
	const onChangeRef = useRef(onChange);
	onChangeRef.current = onChange;
	const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

	// Wire the imperative live-content getter so the dialog can skip the debounce on Save.
	// When content is incompatible (legacy raw HTML), return it UNCHANGED — serializing
	// the garbage-parsed blocks would corrupt the original on Save.
	useEffect(() => {
		if (getContentRef) {
			getContentRef.current = () => (compatible ? actions.getContent() : content);
		}
		return () => {
			if (getContentRef) {
				getContentRef.current = null;
			}
		};
		// actions is stable (useCallback); re-run if compat/content/ref identity changes
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [getContentRef, compatible, content]);

	useEffect(() => {
		const handleKeyDown = (e: KeyboardEvent) => {
			const mod = e.ctrlKey || e.metaKey;
			if (!mod) return;

			// While typing in an input/textarea/contenteditable, the browser's own
			// undo stack must win — hijacking Ctrl+Z here silently swallows text
			// undo in the Subject field and during inline canvas editing.
			const target = e.target as HTMLElement | null;
			if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)) {
				return;
			}

			if (e.key === "z" && !e.shiftKey) {
				e.preventDefault();
				actions.undo();
				return;
			}

			if ((e.key === "z" && e.shiftKey) || e.key === "y") {
				e.preventDefault();
				actions.redo();
			}
		};

		document.addEventListener("keydown", handleKeyDown);
		return () => {
			document.removeEventListener("keydown", handleKeyDown);
		};
	}, [actions]);

	// Notify parent of content changes (debounced 300 ms so rapid edits don't thrash).
	// Missing vars are computed from the group's required var list vs. serialized content.
	useEffect(() => {
		if (!onChangeRef.current) return;
		// Incompatible content (legacy raw HTML): do NOT emit serialized blocks —
		// that would overwrite the original HTML with garbage. Leave content untouched.
		if (!compatible) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);
		debounceRef.current = setTimeout(() => {
			const liveContent = actions.getContent();
			const missing = getRequiredVars(group).filter((v) => !liveContent.includes(v));
			onChangeRef.current?.(liveContent, missing);
		}, 300);
		return () => {
			if (debounceRef.current) clearTimeout(debounceRef.current);
		};
		// state drives getContent — when state changes we schedule a notification
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [state, group, compatible]);

	const handleSaveDraft = async () => {
		if (!onSaveDraft || draftSaving) return;
		setDraftSaving(true);
		try {
			// onSaveDraft may be async (API call) — await so the button stays
			// disabled for the whole request, preventing duplicate version rows.
			await onSaveDraft(actions.getContent());
		} finally {
			setDraftSaving(false);
		}
	};

	const handlePublish = async () => {
		if (!onPublish || publishing) return;
		const liveContent = actions.getContent();
		const missing = actions.missing();
		if (missing.length > 0) {
			setPublishAlert(`Thiếu biến bắt buộc: ${missing.join(", ")}`);
			return;
		}
		setPublishAlert("");
		setPublishing(true);
		try {
			await onPublish(liveContent);
		} finally {
			setPublishing(false);
		}
	};

	const handleOpenHistory = async () => {
		if (!onListVersions) return;
		setHistoryLoading(true);
		try {
			const list = await onListVersions();
			setHistoryVersions(list);
		} finally {
			setHistoryLoading(false);
		}
	};

	const handleRestore = (versionId: number) => {
		setHistoryVersions(null);
		onRestore?.(versionId);
	};

	// Incompatible content (legacy raw HTML) — show a guard instead of dumping
	// escaped HTML into the canvas. User keeps the original via HTML/Easy mode,
	// or regenerates a fresh builder-editable template.
	if (!compatible) {
		return (
			<ShellWrap
				ref={containerRef}
				tabIndex={-1}>
				<div
					data-testid="builder-incompatible"
					style={{ padding: "48px 32px", maxWidth: 560, margin: "0 auto", textAlign: "center" }}>
					<div style={{ fontSize: 40, marginBottom: 12 }}>🧩</div>
					<h3 style={{ margin: "0 0 8px", fontSize: 18, color: "#1f2937" }}>
						Template này đang ở dạng HTML cũ
					</h3>
					<p style={{ margin: "0 0 20px", fontSize: 14, color: "#6b7280", lineHeight: 1.6 }}>
						Builder chỉ mở được template tạo bằng Builder. Nội dung hiện tại là HTML thô nên chưa hiển thị
						trực quan được. Bạn có thể tạo lại email này bằng Builder (sẽ thay nội dung cũ bằng mẫu mới).
					</p>
					{onRegenerate ? (
						<SaveBtn
							onClick={onRegenerate}
							data-testid="btn-regenerate">
							Tạo lại bằng Builder
						</SaveBtn>
					) : null}
				</div>
			</ShellWrap>
		);
	}

	const isPreview = mode === "preview";
	// In preview mode sampleMode is always on for the canvas
	const canvasSample = sampleMode || isPreview;

	const CanvasWrapper = darkMode ? CanvasAreaDark : CanvasArea;

	return (
		<ShellWrap
			ref={containerRef}
			tabIndex={-1}>
			{/* ---- Topbar ---- */}
			<Topbar>
				<TopbarTitle>Email Builder</TopbarTitle>

				{/* Segmented mode toggle */}
				<SegToggle>
					<SegBtn
						active={!isPreview}
						onClick={() => actions.setMode("edit")}>
						Sửa
					</SegBtn>
					<SegBtn
						active={isPreview}
						onClick={() => actions.setMode("preview")}>
						Xem
					</SegBtn>
				</SegToggle>

				{/* Undo / Redo */}
				<IconBtn
					title="Hoàn tác (Ctrl+Z)"
					disabled={!canUndo}
					onClick={actions.undo}
					aria-label="Undo">
					↩
				</IconBtn>
				<IconBtn
					title="Làm lại (Ctrl+Shift+Z)"
					disabled={!canRedo}
					onClick={actions.redo}
					aria-label="Redo">
					↪
				</IconBtn>

				{/* Dark mode */}
				<IconBtn
					title="Chế độ tối"
					active={darkMode}
					onClick={actions.toggleDark}
					aria-label="Toggle dark mode">
					◑
				</IconBtn>

				{/* Sample data */}
				<IconBtn
					title="Dữ liệu mẫu"
					active={sampleMode}
					onClick={actions.toggleSample}
					aria-label="Toggle sample data">
					⊞
				</IconBtn>

				{/* Presets menu */}
				<PresetsMenu actions={actions} />

				<TopbarSpacer />

				{/* Publish alert inline */}
				{publishAlert ? (
					<span
						style={{ fontSize: 12, color: "#b91c1c", maxWidth: 240, lineHeight: 1.3 }}
						data-testid="publish-alert">
						{publishAlert}
					</span>
				) : null}

				{/* Draft */}
				{onSaveDraft ? (
					<SaveBtn
						onClick={handleSaveDraft}
						disabled={draftSaving}
						style={{ background: "#64748b", opacity: draftSaving ? 0.6 : 1 }}
						data-testid="btn-save-draft">
						{draftSaving ? "Đang lưu…" : "Lưu nháp"}
					</SaveBtn>
				) : null}

				{/* Publish */}
				{onPublish ? (
					<SaveBtn
						onClick={handlePublish}
						disabled={publishing}
						style={{ opacity: publishing ? 0.6 : 1 }}
						data-testid="btn-publish">
						{publishing ? "Đang xuất bản…" : "Xuất bản"}
					</SaveBtn>
				) : null}

				{/* Version history */}
				{onListVersions ? (
					<IconBtn
						title="Lịch sử phiên bản"
						onClick={handleOpenHistory}
						disabled={historyLoading}
						data-testid="btn-history"
						aria-label="Version history">
						{historyLoading ? "…" : "⏱"}
					</IconBtn>
				) : null}

				{/* Save (legacy) — hidden when the versioning flow is wired: "Xuất bản"
				    already persists both the version AND the legacy content the send
				    pipeline reads. Two parallel save paths = drift + confusion. */}
				{!onPublish ? <SaveBtn onClick={() => onSave(actions.getContent())}>Lưu</SaveBtn> : null}
			</Topbar>

			{/* ---- 3-column body ---- */}
			<PaneRow>
				{!isPreview && (
					<SidePanel aria-label="Palette">
						<Palette
							catalog={catalog}
							actions={actions}
							onInsertVar={insertChip}
							onInsertSymbol={insertText}
							onInsertSection={() => {
								// Click fallback: insert a section title into the selected
								// summary, else the last summary block. (Drag is the primary way.)
								const sel = state.blocks.find((b) => b.id === state.selId);
								const target =
									sel?.type === "summary"
										? sel
										: [...state.blocks].reverse().find((b) => b.type === "summary");
								if (target?.id) actions.addSectionHeader(target.id);
							}}
							onPaletteMouseDown={rememberFocus}
						/>
					</SidePanel>
				)}

				<CanvasWrapper>
					{isPreview && templateId ? (
						<ServerPreview
							templateId={templateId}
							content={actions.getContent()}
						/>
					) : (
						<Canvas
							state={state}
							sampleMode={canvasSample}
							darkMode={darkMode}
							catalog={catalog}
							mode={mode}
							actions={isPreview ? undefined : actions}
						/>
					)}
				</CanvasWrapper>

				{!isPreview && (
					<SidePanel aria-label="Inspector">
						<Inspector
							state={state}
							actions={actions}
						/>
					</SidePanel>
				)}
			</PaneRow>

			{historyVersions !== null ? (
				<VersionHistoryModal
					versions={historyVersions}
					onRestore={handleRestore}
					onClose={() => setHistoryVersions(null)}
				/>
			) : null}
		</ShellWrap>
	);
}
